import * as React from 'react';
import {Theme} from '../types/Theme';
import {Surface} from '../types/Surface';
import {SurfaceProminence} from '../types/SurfaceProminence';
import {SurfaceState} from '../types/SurfaceState';
import Helpers from '../../shared-common/helpers/Helpers';
import {Func} from '../../shared-common/types/Func';
import {InteractionEffect} from '../types/InteractionEffect';
import ThemeHelpers from '../helpers/ThemeHelpers';
import {SurfaceDensity} from '../types/SurfaceDensity';

export interface ThemeStyle {
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: string;
  textColor?: string;
  highlightColor?: string;
  effectColor?: string;
  rounding?: string;
  fontSize?: string;
  boundarySpacing?: string;
  childSpacing?: string;
  elevation?: string;
}

export interface ThemeProviderProps {
  getCurrentTheme: Func<Theme>;

  getSurfaceStyle(theme: Theme | null | undefined,
                  surface: Surface,
                  effect: InteractionEffect,
                  prominence: SurfaceProminence,
                  density: SurfaceDensity,
                  state: SurfaceState): ThemeStyle;
}

export const ThemeProvider = React.createContext<ThemeProviderProps>({
  getCurrentTheme: () => 'light',

  getSurfaceStyle(theme: Theme,
                  surface: Surface,
                  effect: InteractionEffect,
                  prominence: SurfaceProminence,
                  density: SurfaceDensity,
                  state: SurfaceState
  ): ThemeStyle {

    const requestedTheme = Helpers.ifNil(theme, 'auto') === 'auto' ? 'light' : theme;

    if (requestedTheme === 'unthemed') {
      return {};
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'floating' && effect === 'complete') {
      return ThemeHelpers.createFloatingPanelThemeStyle('#EEF4FF', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'floating' && effect === 'none') {
      return ThemeHelpers.createFloatingPanelThemeStyle('#FFFFFF', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'floating' && effect === 'success') {
      return ThemeHelpers.createFloatingPanelThemeStyle('#E6FFEA', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'floating' && effect === 'warning') {
      return ThemeHelpers.createFloatingPanelThemeStyle('#FFFBE6', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'floating' && effect === 'danger') {
      return ThemeHelpers.createFloatingPanelThemeStyle('#FFE8E6', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'flat' && effect === 'complete') {
      return ThemeHelpers.createFlatPanelThemeStyle('#EEF4FF', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'flat' && effect === 'none') {
      return ThemeHelpers.createFlatPanelThemeStyle('transparent', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'flat' && effect === 'success') {
      return ThemeHelpers.createFlatPanelThemeStyle('#E6FFEA', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'flat' && effect === 'warning') {
      return ThemeHelpers.createFlatPanelThemeStyle('#FFFBE6', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'flat' && effect === 'danger') {
      return ThemeHelpers.createFlatPanelThemeStyle('#FFE8E6', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'elevated' && effect === 'complete') {
      return ThemeHelpers.createElevatedPanelThemeStyle('#EEF4FF', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'elevated' && effect === 'none') {
      return ThemeHelpers.createElevatedPanelThemeStyle('#EAEAEA', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'elevated' && effect === 'success') {
      return ThemeHelpers.createElevatedPanelThemeStyle('#E6FFEA', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'elevated' && effect === 'warning') {
      return ThemeHelpers.createElevatedPanelThemeStyle('#FFFBE6', density);
    }

    if (requestedTheme === 'light' && surface === 'panel' && prominence === 'elevated' && effect === 'danger') {
      return ThemeHelpers.createElevatedPanelThemeStyle('#FFE8E6', density);
    }

    if (requestedTheme === 'light' && surface === 'button' && prominence === 'flat' && effect === 'complete') {
      return ThemeHelpers.createFlatButtonThemeStyle('#2E75B6', '#FFFFFF', density, state);
    }

    if (requestedTheme === 'light' && surface === 'button' && prominence === 'flat' && effect === 'none') {
      return ThemeHelpers.createFlatButtonThemeStyle('#8497B0', '#FFFFFF', density, state);
    }

    if (requestedTheme === 'light' && surface === 'button' && prominence === 'flat' && effect === 'danger') {
      return ThemeHelpers.createFlatButtonThemeStyle('#681938', '#FFFFFF', density, state);
    }

    if (requestedTheme === 'light' && surface === 'button' && prominence === 'outlined' && effect === 'complete') {
      return ThemeHelpers.createFlatButtonThemeStyle('#2E75B6', '#FFFFFF', density, state);
    }

    if (requestedTheme === 'light' && surface === 'button' && prominence === 'outlined' && effect === 'none') {
      return ThemeHelpers.createFlatButtonThemeStyle('#8497B0', '#FFFFFF', density, state);
    }

    if (requestedTheme === 'light' && surface === 'button' && prominence === 'outlined' && effect === 'danger') {
      return ThemeHelpers.createFlatButtonThemeStyle('#681938', '#FFFFFF', density, state);
    }

    if (requestedTheme === 'light' && surface === 'button' && prominence === 'elevated' && effect === 'complete') {
      return ThemeHelpers.createElevatedButtonThemeStyle('#2E75B6', '#FFFFFF', density, state);
    }

    if (requestedTheme === 'light' && surface === 'button' && prominence === 'elevated' && effect === 'none') {
      return ThemeHelpers.createElevatedButtonThemeStyle('#8497B0', '#FFFFFF', density, state);
    }

    if (requestedTheme === 'light' && surface === 'button' && prominence === 'elevated' && effect === 'danger') {
      return ThemeHelpers.createElevatedButtonThemeStyle('#681938', '#FFFFFF', density, state);
    }

    if (requestedTheme === 'light' && surface === 'search' && prominence === 'outlined') {
      return {
        borderColor: '#8497B0',
        borderWidth: '1px',
        rounding: '4px',
        boundarySpacing: '4px',
        effectColor: 'rgba(24,144,255,0.2)',
        highlightColor: '#1890ff'
      };
    }

    if (requestedTheme === 'light' && surface === 'input' && prominence === 'outlined') {
      return ThemeHelpers.createOutlinedInputThemeStyle('#8497B0', state);
    }

    throw new Error(`Surface type not recognised (${JSON.stringify({
      theme,
      surface,
      effect,
      prominence,
      density,
      state
    })})`);
  }
});
